import { useAppSelector } from "@/app/hooks";
import {
  getInteractiveToken,
  getSurveyGroupId,
  getSurveyId,
} from "@/services/interactiveSurveyAuth";
import { Navigate, Outlet, useParams } from "react-router";

const ProtectedRoute = () => {
  // Interactive Survey Authentication state
  const interactiveSurveyAuth = useAppSelector(
    (state) => state.interactiveSurvey,
  );

  const { surveyId: surveyIdFromUrl, surveyGroupId: surveyGroupIdFromUrl } =
    useParams<{ surveyId?: string; surveyGroupId?: string }>();

  // Check for existing interactive survey token
  const interactiveToken = getInteractiveToken();
  const storedSurveyId = getSurveyId();
  const storedSurveyGroupId = getSurveyGroupId();

  if (interactiveSurveyAuth.isLoading) {
    return <div>Loading...</div>;
  }

  // Determine the survey/group ID to use (from URL or stored)
  const currentSurveyId = surveyIdFromUrl || storedSurveyId;
  const currentSurveyGroupId = surveyGroupIdFromUrl || storedSurveyGroupId;

  // Require interactive survey authentication for all protected routes
  if (!interactiveSurveyAuth.isAuthenticated && !interactiveToken) {
    // Group-scoped routes, or a member survey reached via a group session,
    // redirect to group login so the user stays within the group.
    if (surveyGroupIdFromUrl || currentSurveyGroupId) {
      return (
        <Navigate
          to={`/interactive/group/${currentSurveyGroupId}/login`}
          replace
        />
      );
    }

    // If no survey ID is available, show error
    if (!currentSurveyId) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center max-w-md">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">
              Survey Access Required
            </h1>
            <p className="text-gray-600 mb-4">
              Please access this application through a survey link that includes
              a valid survey ID.
            </p>
            <p className="text-sm text-gray-500">
              Expected format: /interactive/YOUR_SURVEY_ID/login
            </p>
          </div>
        </div>
      );
    }
    // Redirect to login with new URL pattern
    return <Navigate to={`/interactive/${currentSurveyId}/login`} replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
